import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useChainId } from 'wagmi'

import { getDepositOptions } from './assets'
import { calculateUpdatePositionOverview } from './calculateUpdatePositionOverview'
import { getCollateralType } from './collateralization'
import { filterRelatedCampaigns } from './filterRelatedCampaigns'
import { getDepositDialogFormValidator, getFormFieldsForDepositDialog } from './form'
import { PositionOverview } from './types'
import { useCreateObjectives } from './useCreateObjectives'
import { getNativeAssetInfo } from '@/config/chain/utils/getNativeAssetInfo'
import { TokenWithBalance, TokenWithValue } from '@/domain/common/types'
import { Token } from '@/domain/types/Token'
import { Objective } from '@/features/actions/logic/types'
import { useActions } from '@/features/actions/logic/useActions'
import { ActionsViewState } from '@/features/actions/types'
import { AssetInputSchema, useDebouncedDialogFormValues } from '@/features/dialogs/common/logic/form'
import { FormFieldsForDialog, PageState, PageStatus } from '@/features/dialogs/common/types'
import { useOngoingCampaignsQuery } from '@/features/spark-rewards/data/useOngoingCampaignsQuery'
import { useAaveDataLayer } from '@/features/sparklend/common/data/market-info/aave-data-layer/useAaveDataLayer'
import { EPOCH_LENGTH } from '@/features/sparklend/common/data/market-info/consts'
import { useMarketInfo } from '@/features/sparklend/common/data/market-info/useMarketInfo'
import { useMarketWalletInfo } from '@/features/sparklend/common/data/useMarketWalletInfo'
import { SparkRewardCampaign } from '@/ui/molecules/spark-reward-pill/SparkRewardPill'

export interface UseDepositDialogOptions {
  initialToken: Token
}

export interface UseDepositDialogResult {
  depositableAssets: TokenWithBalance[]
  assetsToDepositFields: FormFieldsForDialog
  tokenToDeposit: TokenWithValue
  objectives: Objective[]
  pageStatus: PageStatus
  form: UseFormReturn<AssetInputSchema>
  currentPositionOverview: PositionOverview
  updatedPositionOverview?: PositionOverview
  relatedCampaigns: SparkRewardCampaign[]
  actionsViewState: ActionsViewState
}

export function useDepositDialog({ initialToken }: UseDepositDialogOptions): UseDepositDialogResult {
  const chainId = useChainId()
  const { aaveData } = useAaveDataLayer({ chainId })
  const { marketInfo } = useMarketInfo({ chainId })
  const { marketInfo: marketInfoIn1Epoch } = useMarketInfo({ timeAdvance: EPOCH_LENGTH, chainId })
  const walletInfo = useMarketWalletInfo({ chainId })
  const ongoingCampaignsResult = useOngoingCampaignsQuery()

  const nativeAssetInfo = getNativeAssetInfo(marketInfo.chainId)

  const [pageStatus, setPageStatus] = useState<PageState>('form')

  const form = useForm<AssetInputSchema>({
    resolver: zodResolver(getDepositDialogFormValidator(walletInfo, marketInfo)),
    defaultValues: {
      symbol: initialToken.symbol,
      value: '',
    },
    mode: 'onChange',
  })
  const assetsToDepositFields = getFormFieldsForDepositDialog({
    form,
    marketInfo: marketInfoIn1Epoch,
    walletInfo,
  })
  const {
    debouncedFormValues: tokenToDeposit,
    isDebouncing,
    isFormValid,
  } = useDebouncedDialogFormValues({
    form,
    marketInfo,
  })

  const marketToken = marketInfo.findOneReserveBySymbol(initialToken.symbol).token
  const depositableAssets = getDepositOptions({
    token: marketToken,
    marketInfo,
    walletInfo,
    nativeAssetInfo,
  })

  const collateralType = getCollateralType({
    position: tokenToDeposit.position,
    summary: marketInfo.userPositionSummary,
    userConfiguration: marketInfo.userConfiguration,
  })

  const depositPosition = marketInfo.findOnePositionByToken(tokenToDeposit.token)
  const currentPositionOverview: PositionOverview = {
    healthFactor: marketInfo.userPositionSummary.healthFactor,
    collateralization: collateralType,
    supplyAPY: tokenToDeposit.reserve.supplyAPY,
    supplied: depositPosition.collateralBalance,
  }
  const updatedPositionOverview = calculateUpdatePositionOverview({
    tokenToDeposit,
    marketInfo,
    aaveData,
    nativeAssetInfo,
    currentPositionOverview,
  })

  const objectives = useCreateObjectives(tokenToDeposit)

  const relatedCampaigns = filterRelatedCampaigns({
    ongoingCampaignsResult,
    token: initialToken,
    chainId,
  })

  const actionsEnabled = tokenToDeposit.value.gt(0) && isFormValid && !isDebouncing

  const { viewState: actionsViewState, reset: resetActions } = useActions({
    objectives,
    context: {
      chainId,
    },
    onFinish: () => setPageStatus('success'),
    enabled: actionsEnabled,
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to reset actions when the form values change
  useEffect(() => {
    resetActions()
  }, [tokenToDeposit.token.symbol, tokenToDeposit.value.toString(), tokenToDeposit.isMaxSelected, resetActions])

  return {
    depositableAssets,
    assetsToDepositFields,
    tokenToDeposit,
    objectives,
    pageStatus: {
      state: pageStatus,
      actionsEnabled,
      goToSuccessScreen: () => setPageStatus('success'),
    },
    form,
    currentPositionOverview,
    updatedPositionOverview,
    relatedCampaigns,
    actionsViewState,
  }
}
